/**
 * BundleTabContent Component
 *
 * Renders the content for a single tab in the Add-on Bundles page.
 * Displays hero section, feature sections, and FAQ accordion.
 */

import React, { useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import type { AddOnBundleTab, BillingInterval, BundleWithStatus } from '../../types';

interface BundleTabContentProps {
  tab: AddOnBundleTab;
  billingInterval?: BillingInterval;
  addOns?: BundleWithStatus[];
  showAddToPlan?: boolean;
  onAddToPlanClick?: () => void;
  onTalkToSalesClick?: () => void;
}

/**
 * Hero Section Props
 */
interface HeroSectionProps {
  hero: AddOnBundleTab['hero'];
  bundlePrice?: { monthly: number; yearly: number };
  billingInterval?: BillingInterval;
  showTalkToSales?: boolean;
  calendlyUrl?: string;
  showAddToPlan?: boolean;
  onTalkToSalesClick?: () => void;
  onAddToPlanClick?: () => void;
}

/**
 * Hero Section - Title, subtitle, description, optional pricing, and CTAs
 */
const HeroSection: React.FC<HeroSectionProps> = ({
  hero,
  bundlePrice,
  billingInterval = 'month',
  showTalkToSales,
  calendlyUrl,
  showAddToPlan,
  onTalkToSalesClick,
  onAddToPlanClick,
}) => {
  const theme = useTheme();
  const hasCtaButtons = (showTalkToSales && calendlyUrl) || showAddToPlan;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 4,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.background.paper} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {hero.title}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          color: theme.palette.primary.main,
          mb: 2,
        }}
      >
        {hero.subtitle}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          mb: hero.pricing ? 3 : 0,
          maxWidth: '800px',
          lineHeight: 1.7,
        }}
      >
        {hero.description}
      </Typography>
      {(hero.pricing || bundlePrice) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          {/* Left: Pricing text (from config) */}
          {hero.pricing && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                Pricing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hero.pricing}
              </Typography>
            </Box>
          )}

          {/* Right: Actual price (from catalog) - always show monthly equivalent */}
          {bundlePrice && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                $
                {(billingInterval === 'year'
                  ? Math.round(bundlePrice.yearly / 12)
                  : bundlePrice.monthly) / 100}
                <Typography component="span" variant="body1" color="text.secondary">
                  /mo
                </Typography>
              </Typography>
              {billingInterval === 'year' && (
                <Typography variant="caption" color="text.secondary">
                  Billed annually
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* CTA Buttons - Bottom Right */}
      {hasCtaButtons && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
            pt: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" spacing={1.5}>
            {showTalkToSales && calendlyUrl && (
              <Button
                component="a"
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="primary"
                startIcon={<CalendarMonthIcon />}
                onClick={onTalkToSalesClick}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Talk to Sales
              </Button>
            )}
            {showAddToPlan && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={onAddToPlanClick}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add To Plan
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

/**
 * Feature Card - Individual feature with description and bullet points
 */
const FeatureCard: React.FC<{
  title: string;
  description: string;
  bullets?: string[];
}> = ({ title, description, bullets }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        '&:last-child': {
          mb: 0,
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          mb: bullets && bullets.length > 0 ? 2 : 0,
          lineHeight: 1.6,
        }}
      >
        {description}
      </Typography>
      {bullets && bullets.length > 0 && (
        <List dense disablePadding>
          {bullets.map((bullet, index) => (
            <ListItem key={index} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: '1rem', color: theme.palette.success.main }}
                />
              </ListItemIcon>
              <ListItemText
                primary={bullet}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.primary',
                  lineHeight: 1.5,
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

/**
 * Section - Groups features under a title with optional description
 */
const Section: React.FC<{
  title: string;
  description?: string;
  features?: AddOnBundleTab['sections'][0]['features'];
}> = ({ title, description, features }) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}
      {features && features.length > 0 && (
        <Box>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              title={feature.title}
              description={feature.description}
              bullets={feature.bullets}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * FAQ Section - Accordion-style frequently asked questions
 */
const FaqSection: React.FC<{ faq: AddOnBundleTab['faq'] }> = ({ faq }) => {
  const theme = useTheme();

  if (!faq || faq.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 4 }} />
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 3,
        }}
      >
        Frequently asked questions
      </Typography>
      {faq.map((item, index) => (
        <Accordion
          key={index}
          elevation={0}
          disableGutters
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            mb: 1,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
              mb: 1,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 3,
              '& .MuiAccordionSummary-content': {
                my: 2,
              },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {item.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

/**
 * Main BundleTabContent Component
 */
export const BundleTabContent: React.FC<BundleTabContentProps> = ({
  tab,
  billingInterval = 'month',
  addOns,
  showAddToPlan,
  onAddToPlanClick,
  onTalkToSalesClick,
}) => {
  // Look up bundle price by name (case-insensitive)
  const bundlePrice = useMemo(() => {
    if (!addOns) return undefined;
    const bundleName =
      'bundleName' in tab && typeof tab.bundleName === 'string' ? tab.bundleName : undefined;
    if (!bundleName) return undefined;
    const bundleNameLower = bundleName.toLowerCase();
    const bundle = addOns.find(b => b.name.toLowerCase() === bundleNameLower);
    if (!bundle) return undefined;
    return { monthly: bundle.monthly_unit_amount, yearly: bundle.yearly_unit_amount };
  }, [tab, addOns]);

  return (
    <Box sx={{ py: 3 }}>
      {/* Hero Section with CTAs */}
      <HeroSection
        hero={tab.hero}
        bundlePrice={bundlePrice}
        billingInterval={billingInterval}
        showTalkToSales={tab.showTalkToSales}
        calendlyUrl={tab.calendlyUrl}
        showAddToPlan={showAddToPlan}
        onTalkToSalesClick={onTalkToSalesClick}
        onAddToPlanClick={onAddToPlanClick}
      />

      {/* Content Sections */}
      {tab.sections.map((section, index) => (
        <Section
          key={index}
          title={section.title}
          description={section.description}
          features={section.features}
        />
      ))}

      {/* FAQ Section */}
      <FaqSection faq={tab.faq} />
    </Box>
  );
};

export default BundleTabContent;
